import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface BiometricData {
  timestamp: string;
  heartRate: number;
  hrv: number;
  coherence: number;
  brainwaves: {
    alpha: number;
    beta: number;
    theta: number;
    delta: number;
  };
}

interface BiometricHistoryChartProps {
  data: BiometricData[];
  metric: 'heartRate' | 'hrv' | 'coherence' | 'brainwaves';
}

export default function BiometricHistoryChart({ data, metric }: BiometricHistoryChartProps) {
  const formatData = () => {
    if (metric === 'brainwaves') {
      return data.map(item => ({
        time: new Date(item.timestamp).toLocaleTimeString(),
        Alpha: item.brainwaves.alpha,
        Beta: item.brainwaves.beta,
        Theta: item.brainwaves.theta,
        Delta: item.brainwaves.delta
      }));
    }

    return data.map(item => ({
      time: new Date(item.timestamp).toLocaleTimeString(),
      value: item[metric]
    }));
  };

  const getTitle = () => {
    switch (metric) {
      case 'heartRate': return 'Heart Rate Over Time';
      case 'hrv': return 'Heart Rate Variability';
      case 'coherence': return 'Heart Coherence';
      case 'brainwaves': return 'Brainwave Activity';
      default: return 'Biometric Data';
    }
  };

  const chartData = formatData();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">{getTitle()}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="time" fontSize={10} />
            <YAxis fontSize={10} />
            <Tooltip />
            {metric === 'brainwaves' ? (
              <>
                <Line type="monotone" dataKey="Alpha" stroke="#8884d8" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Beta" stroke="#82ca9d" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Theta" stroke="#ffc658" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Delta" stroke="#ff7300" strokeWidth={2} dot={false} />
                <Legend />
              </>
            ) : (
              <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} dot={false} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}